/**
 * A use case on disk: steps that are calls (an operationId and its arguments,
 * as an MCP tool takes them), each with the answer it expects and the values
 * it saves for later steps. `{{name}}` in arguments and expectations is a
 * variable: saved from an earlier answer, declared in `vars`, or one the
 * runner provides (`day`, `run`, the personas' workspaces A, B and C). A
 * whole-string `{{name}}` keeps its value's JSON type.
 *
 * This module reads and checks the shape, substitutes variables, matches
 * answers against expectations, and scrubs anything secret before a use case
 * leaves the browser. Pure.
 */

import type { Json, Persona, Step, UseCase, UseCaseOrigin } from '../contracts.ts';
import { isObj } from '../model/json.ts';

export const VAR = /\{\{\s*([A-Za-z0-9_.:+-]+)\s*\}\}/g;
const WHOLE = /^\{\{\s*([A-Za-z0-9_.:+-]+)\s*\}\}$/;
const SLUG = /^[a-z0-9][a-z0-9._-]{0,62}$/;
const PERSONAS: readonly Persona[] = ['A', 'B', 'C'];
const ORIGINS: readonly UseCaseOrigin[] = ['builtin', 'recorded', 'director', 'imported'];

/** Secrets as the API mints them (keys, operator keys, webhook secrets, verification tokens), and the user's own Anthropic key. */
export const SECRET = /\b(?:sbk|sbo|whsec|sbv)_[A-Za-z0-9_]+|sk-ant-[A-Za-z0-9_-]+/g;
/** Fields that hold a secret whatever their value looks like. */
const SECRET_FIELD = /secret|^anthropickey$/i;

export class MissingVar extends Error {
  constructor(readonly variable: string) { super(`no value for {{${variable}}}`); }
}


/** Is this JSON? (No undefined, functions, bigints or cycles: what JSON.parse could have given.) */
const isJson = (v: unknown, depth = 0): v is Json => {
  if (depth > 64) return false;
  if (v === null || typeof v === 'string' || typeof v === 'boolean') return true;
  if (typeof v === 'number') return Number.isFinite(v);
  if (Array.isArray(v)) return v.every((x) => isJson(x, depth + 1));
  if (isObj(v)) return Object.values(v).every((x) => isJson(x, depth + 1));
  return false;
};

const stepError = (i: number, why: string) => ({ error: `step ${i + 1}: ${why}` });

const parseStep = (raw: unknown, i: number): Step | { error: string } => {
  if (!isObj(raw)) return stepError(i, 'must be an object');
  const { as, op, args, expect, save, ensure, say, verb } = raw;
  if (as !== undefined && !PERSONAS.includes(as as Persona)) return stepError(i, '"as" must be A, B or C');
  if (typeof op !== 'string' || !/^[A-Za-z0-9_-]{1,64}$/.test(op)) return stepError(i, '"op" must be an operationId');
  if (!isObj(args) || !isJson(args)) return stepError(i, '"args" must be an object of JSON values');
  const step: Step = { op, args: args as Record<string, Json> };
  if (as !== undefined) step.as = as as Persona;
  if (expect !== undefined) {
    if (!isObj(expect)) return stepError(i, '"expect" must be an object');
    const status = expect.status;
    const statuses = Array.isArray(status) ? status : [status];
    if (statuses.length === 0 || !statuses.every((s) => Number.isInteger(s) && (s as number) >= 100 && (s as number) <= 599)) {
      return stepError(i, '"expect.status" must be an HTTP status or a list of them');
    }
    if (expect.body !== undefined && !isJson(expect.body)) return stepError(i, '"expect.body" must be JSON');
    step.expect = { status: status as number | number[], ...(expect.body === undefined ? {} : { body: expect.body as Json }) };
  }
  if (save !== undefined) {
    if (!isObj(save) || !Object.entries(save).every(([k, v]) => /^[A-Za-z0-9_]+$/.test(k) && typeof v === 'string')) {
      return stepError(i, '"save" maps variable names to paths like "body.id"');
    }
    step.save = save as Record<string, string>;
  }
  if (ensure !== undefined) {
    if (typeof ensure !== 'boolean') return stepError(i, '"ensure" must be true or false');
    step.ensure = ensure;
  }
  if (say !== undefined) {
    if (typeof say !== 'string') return stepError(i, '"say" must be text');
    step.say = say.slice(0, 280);
  }
  if (verb !== undefined) {
    if (typeof verb !== 'string') return stepError(i, '"verb" must be a gesture id');
    step.verb = verb;
  }
  return step;
};

/** A use case read from anything, checked field by field; the first fault, in words, otherwise. */
export const parseUseCase = (raw: unknown): UseCase | { error: string } => {
  if (!isObj(raw)) return { error: 'a use case is a JSON object' };
  if (raw.v !== 1) return { error: 'this is not a version 1 use case ("v": 1)' };
  const { id, title, blurb, origin, personas, worlds, vars, steps } = raw;
  if (typeof id !== 'string' || !SLUG.test(id)) return { error: '"id" must be a short lowercase name' };
  if (typeof title !== 'string' || title.length === 0 || title.length > 120) return { error: '"title" must be 1–120 characters' };
  if (typeof blurb !== 'string' || blurb.length > 600) return { error: '"blurb" must be text of at most 600 characters' };
  if (!ORIGINS.includes(origin as UseCaseOrigin)) return { error: `"origin" must be one of ${ORIGINS.join(', ')}` };
  if (!Array.isArray(personas) || personas.length === 0 || !personas.every((p) => PERSONAS.includes(p as Persona))) {
    return { error: '"personas" must list some of A, B, C' };
  }
  if (!Array.isArray(worlds) || !worlds.every((w) => typeof w === 'string')) return { error: '"worlds" must be a list of names' };
  if (vars !== undefined && (!isObj(vars) || !Object.entries(vars).every(([k, v]) => /^[A-Za-z0-9_]+$/.test(k) && typeof v === 'string'))) {
    return { error: '"vars" maps names to text' };
  }
  if (!Array.isArray(steps) || steps.length === 0 || steps.length > 500) return { error: '"steps" must list 1–500 steps' };
  const parsed: Step[] = [];
  for (const [i, s] of steps.entries()) {
    const step = parseStep(s, i);
    if ('error' in step) return step;
    parsed.push(step);
  }
  return {
    v: 1, id, title, blurb, origin: origin as UseCaseOrigin, personas: [...new Set(personas as Persona[])],
    worlds: worlds as string[], ...(vars === undefined ? {} : { vars: vars as Record<string, string> }), steps: parsed,
  };
};

export const parseText = (text: string): UseCase | { error: string } => {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { error: 'that file is not JSON' };
  }
  return parseUseCase(raw);
};

// -- variables ----------------------------------------------------------------------------

/** Every `{{name}}` a value mentions. */
export const varsIn = (value: Json | undefined): string[] => {
  const out = new Set<string>();
  const walk = (v: Json | undefined): void => {
    if (typeof v === 'string') for (const m of v.matchAll(VAR)) out.add(m[1]!);
    else if (Array.isArray(v)) v.forEach(walk);
    else if (v && typeof v === 'object') Object.values(v).forEach(walk);
  };
  walk(value);
  return [...out];
};

/** Substitute `{{name}}`s; a whole-string one keeps its value's type. Throws `MissingVar`. */
export const substitute = (value: Json, lookup: (name: string) => Json | undefined): Json => {
  if (typeof value === 'string') {
    const whole = WHOLE.exec(value);
    if (whole) {
      const v = lookup(whole[1]!);
      if (v === undefined) throw new MissingVar(whole[1]!);
      return v;
    }
    return value.replace(VAR, (_, name: string) => {
      const v = lookup(name);
      if (v === undefined) throw new MissingVar(name);
      return typeof v === 'string' ? v : JSON.stringify(v);
    });
  }
  if (Array.isArray(value)) return value.map((v) => substitute(v, lookup));
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, substitute(v, lookup)]));
  return value;
};

// -- answers --------------------------------------------------------------------------------

/** A value in an answer by path: "body.legs[0].pieces[1].token"; `[-1]` is the last. */
export const pick = (from: Json | undefined, path: string): Json | undefined => {
  let at: Json | undefined = from;
  for (const part of path.split('.')) {
    const m = /^([^[\]]*)((?:\[-?\d+\])*)$/.exec(part);
    if (!m) return undefined;
    if (m[1]) {
      if (!at || typeof at !== 'object' || Array.isArray(at)) return undefined;
      at = at[m[1]];
    }
    for (const idx of m[2]!.matchAll(/\[(-?\d+)\]/g)) {
      if (!Array.isArray(at)) return undefined;
      const i = Number(idx[1]);
      at = at[i < 0 ? at.length + i : i];
    }
  }
  return at;
};

/** The first path at which `target` appears in `value` (a string or number), for saving it. */
export const pathTo = (value: Json | undefined, target: Json, prefix = 'body'): string | null => {
  if (value === target) return prefix;
  if (Array.isArray(value)) {
    for (const [i, v] of value.entries()) {
      const p = pathTo(v, target, `${prefix}[${i}]`);
      if (p) return p;
    }
  } else if (value && typeof value === 'object') {
    for (const [k, v] of Object.entries(value)) {
      if (!/^[A-Za-z0-9_]+$/.test(k)) continue;
      const p = pathTo(v, target, `${prefix}.${k}`);
      if (p) return p;
    }
  }
  return null;
};

export interface Mismatch { readonly at: string; readonly want: Json; readonly got: Json | null }

/**
 * Does `got` meet `want`? Objects need only the keys listed; arrays must be as
 * long and match element by element, unless written `{"$contains": [...]}`,
 * which asks only that each listed element matches one somewhere.
 */
export const matches = (want: Json, got: Json | undefined, at = 'body', out: Mismatch[] = []): Mismatch[] => {
  if (want && typeof want === 'object' && !Array.isArray(want) && Array.isArray(want.$contains) && Object.keys(want).length === 1) {
    if (!Array.isArray(got)) { out.push({ at, want, got: got ?? null }); return out; }
    for (const [i, w] of want.$contains.entries()) {
      if (!got.some((g) => matches(w, g, at).length === 0)) out.push({ at: `${at} contains [${i}]`, want: w, got });
    }
    return out;
  }
  if (Array.isArray(want)) {
    if (!Array.isArray(got) || got.length !== want.length) { out.push({ at, want, got: got ?? null }); return out; }
    want.forEach((w, i) => matches(w, got[i], `${at}[${i}]`, out));
    return out;
  }
  if (want && typeof want === 'object') {
    if (!got || typeof got !== 'object' || Array.isArray(got)) { out.push({ at, want, got: got ?? null }); return out; }
    for (const [k, v] of Object.entries(want)) matches(v, got[k], `${at}.${k}`, out);
    return out;
  }
  if (want !== got) out.push({ at, want, got: got ?? null });
  return out;
};

// -- scrubbing ------------------------------------------------------------------------------

/** A path that would capture a secret. */
export const capturesSecret = (path: string): boolean => /secret/i.test(path);

/** A value that holds a secret: a key-shaped string anywhere in it, or a field named for one. */
export const holdsSecret = (v: Json | undefined): boolean => {
  if (typeof v === 'string') return new RegExp(SECRET.source).test(v);
  if (Array.isArray(v)) return v.some(holdsSecret);
  if (v && typeof v === 'object') return Object.entries(v).some(([k, x]) => (SECRET_FIELD.test(k) && x !== null) || holdsSecret(x));
  return false;
};

const scrubValue = (v: Json): Json => {
  if (typeof v === 'string') return v.replace(SECRET, '(redacted)');
  if (Array.isArray(v)) return v.map(scrubValue);
  if (v && typeof v === 'object') {
    return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, /secret/i.test(k) ? '(shown once)' : SECRET_FIELD.test(k) ? '(redacted)' : scrubValue(x)]));
  }
  return v;
};

/** The use case with every key-like string redacted and no step saving a secret. */
export const scrub = (uc: UseCase): UseCase => ({
  ...uc,
  title: uc.title.replace(SECRET, '(redacted)'),
  blurb: uc.blurb.replace(SECRET, '(redacted)'),
  ...(uc.vars ? { vars: Object.fromEntries(Object.entries(uc.vars).map(([k, v]) => [k, v.replace(SECRET, '(redacted)')])) } : {}),
  steps: uc.steps.map((s) => {
    const save = s.save ? Object.fromEntries(Object.entries(s.save).filter(([, path]) => !capturesSecret(path))) : undefined;
    const out: Step = { ...s, args: scrubValue(s.args) as Record<string, Json> };
    if (s.expect) out.expect = { status: s.expect.status, ...(s.expect.body === undefined ? {} : { body: scrubValue(s.expect.body) }) };
    if (save && Object.keys(save).length > 0) out.save = save;
    else delete out.save;
    if (s.say) out.say = s.say.replace(SECRET, '(redacted)');
    return out;
  }),
});

export const exportText = (uc: UseCase): string => `${JSON.stringify(scrub(uc), null, 2)}\n`;

// -- the offline reel -------------------------------------------------------------------------

/** A recorded run for the offline preview: the contract's Reel. */
export type { Reel } from '../contracts.ts';
